import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2, FileUp, Upload, Eye, Download, FileText, HelpCircle } from 'lucide-react';
import ConfirmDelete from '../common/ConfirmDelete';
import FileDropArea from '../common/FileDropArea';
import AddActivityForDocumentButton from '../common/AddActivityForDocumentButton';
import { Button, Modal, DateInput, EmptyState } from '../../common';
import UserSelect from '../common/UserSelect';
import { SearchableSelect } from '../../common/SearchableSelect';
import { getProcurements, createProcurement, updateProcurement, deleteProcurement, deleteDocument, getProcurement, addProcurementDocument, updateProcurementDocument, uploadActivityDocument } from '../../../services/dossier.service';
import { downloadAttachmentFile } from '../../../services/attachments.service';
import { getProcurementDocTypes, type ProcurementDocType } from '../../../services/nomenclatoare.service';
import type { Procurement, ProcurementActivity } from '../../../types';

// Tip local minimal: contractele generate din achiziție (câmp nou expus de GET /procurements).
// Definit aici pentru a nu modifica src/types/index.ts (gestionat de alt modul).
interface LinkedContract {
  id: number;
  contract_number?: string;
  contract_name?: string;
}
type ProcurementRow = Procurement & { linked_contracts?: LinkedContract[] };

interface Props {
  dossierId: number;
  projectId: number;
  summary: { total: number; completed: number; inProgress: number };
  statusColor: 'green' | 'yellow' | 'gray' | 'red';
  onRefresh: () => void;
}

export default function AchizitiiSection({ dossierId, projectId, summary, statusColor, onRefresh }: Props) {
  const [procurements, setProcurements] = useState<ProcurementRow[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<Procurement | null>(null);
  const [docTypes, setDocTypes] = useState<ProcurementDocType[]>([]);

  // Add procurement modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [wizardData, setWizardData] = useState({ name: '', estimated_value: '', notes: '', start_date: '', end_date: '', responsible_user_id: null as number | null });
  const [creating, setCreating] = useState(false);
  // Progressive disclosure: câmpurile opționale sunt ascunse implicit ca formularul să nu intimideze.
  const [procDetailsOpen, setProcDetailsOpen] = useState(false);
  const [addDocDetailsOpen, setAddDocDetailsOpen] = useState(false);

  // Edit procurement modal
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState({ name: '', estimated_value: '', notes: '', start_date: '', end_date: '', responsible_user_id: null as number | null });
  const [saving, setSaving] = useState(false);

  // Add document modal
  const [addDocProcId, setAddDocProcId] = useState<number | null>(null);
  // Când modalul de upload e deschis pentru un TIP anume de document (butonul
  // „Încarcă fișier" de pe o activitate), tipul e fix: se adaugă doar fișiere
  // pe acel tip. Selectorul de tip apare doar la „Adaugă document" generic.
  const [addDocTypeLocked, setAddDocTypeLocked] = useState(false);
  const [addDocForm, setAddDocForm] = useState({ document_type_id: 0, title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [addDocFiles, setAddDocFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Edit document modal
  const [editDocId, setEditDocId] = useState<number | null>(null);
  const [editDocData, setEditDocData] = useState({ document_type_id: 0, title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
  const [editDocInitialTypeId, setEditDocInitialTypeId] = useState(0);
  const [editDocFiles, setEditDocFiles] = useState<File[]>([]);
  const [editDocActId, setEditDocActId] = useState<number | null>(null);
  const [editDocGroupIds, setEditDocGroupIds] = useState<number[]>([]);
  const [savingDoc, setSavingDoc] = useState(false);

  // Delete confirmation for documents
  const [pendingDocDelete, setPendingDocDelete] = useState<{ action: () => void; message: string } | null>(null);

  const loadDocTypes = async () => {
    try {
      const data = await getProcurementDocTypes();
      setDocTypes(data.filter(d => d.is_active));
    } catch (err) {
      console.error(err);
      toast.error('Nu s-au putut încărca tipurile de documente.');
    }
  };

  const loadProcurements = async () => {
    try {
      const data = await getProcurements(dossierId);
      setProcurements(data);
      if (data.length === 1 && expanded === null) {
        setExpanded(data[0].id);
        loadDetail(data[0].id);
      }
    } catch (err) {
      console.error(err);
      toast.error('Nu s-au putut încărca dosarele de achiziție.');
    }
    finally { setInitialLoading(false); }
  };

  const loadDetail = async (procId: number) => {
    try {
      const detail = await getProcurement(dossierId, procId);
      setExpandedDetail(detail);
    } catch (err) {
      console.error(err);
      toast.error('Nu s-au putut încărca detaliile dosarului de achiziție.');
    }
  };

  useEffect(() => { loadProcurements(); loadDocTypes(); }, [dossierId]);

  const handleCreate = async () => {
    if (!wizardData.name) return;
    setCreating(true);
    try {
      await createProcurement(dossierId, {
        name: wizardData.name,
        estimated_value: wizardData.estimated_value ? parseFloat(wizardData.estimated_value) : undefined,
        notes: wizardData.notes || undefined,
        start_date: wizardData.start_date || undefined,
        end_date: wizardData.end_date || undefined,
        responsible_user_id: wizardData.responsible_user_id || undefined,
      });
      setShowAddModal(false);
      setWizardData({ name: '', estimated_value: '', notes: '', start_date: '', end_date: '', responsible_user_id: null });
      await loadProcurements();
      onRefresh();
      toast.success('Dosar de achiziție creat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu s-a putut crea dosarul de achiziție. Încearcă din nou.');
    }
    finally { setCreating(false); }
  };

  const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';

  const startEdit = (proc: Procurement) => {
    setEditingId(proc.id);
    // La editare deschidem detaliile dacă există deja date opționale, ca să fie vizibile.
    setProcDetailsOpen(
      proc.estimated_value != null || !!proc.notes || !!proc.start_date || !!proc.end_date || !!proc.responsible_user_id
    );
    setEditData({
      name: proc.name || '',
      estimated_value: proc.estimated_value != null ? String(proc.estimated_value) : '',
      notes: proc.notes || '',
      start_date: toDateStr(proc.start_date),
      end_date: toDateStr(proc.end_date),
      responsible_user_id: proc.responsible_user_id || null,
    });
  };

  const handleUpdate = async () => {
    if (!editingId || !editData.name) return;
    setSaving(true);
    try {
      await updateProcurement(dossierId, editingId, {
        name: editData.name,
        estimated_value: editData.estimated_value ? parseFloat(editData.estimated_value) : undefined,
        notes: editData.notes || undefined,
        start_date: editData.start_date || undefined,
        end_date: editData.end_date || undefined,
        responsible_user_id: editData.responsible_user_id || undefined,
      });
      setEditingId(null);
      await loadProcurements();
      if (expanded === editingId) await loadDetail(editingId);
      onRefresh();
      toast.success('Dosar de achiziție actualizat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu s-au putut salva modificările. Încearcă din nou.');
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (proc: Procurement) => {
    try {
      await deleteProcurement(dossierId, proc.id);
      if (expanded === proc.id) { setExpanded(null); setExpandedDetail(null); }
      await loadProcurements();
      onRefresh();
      toast.success('Dosar de achiziție șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu s-a putut șterge dosarul de achiziție. Încearcă din nou.');
    }
  };

  const handleDeleteDoc = async (docId: number) => {
    try {
      await deleteDocument(docId);
      if (expanded) await loadDetail(expanded);
      toast.success('Fișier șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu s-a putut șterge fișierul. Încearcă din nou.');
    }
  };

  // Tipul curent al documentului: coloana varchar document_type conține codul tipului.
  // Dacă lipsește, se folosește codul activității sub care e listat documentul.
  const resolveDocTypeId = (doc: any, activityId: number) => {
    let code: string = doc.document_type || '';
    if (!code && expandedDetail) {
      const act = (expandedDetail.activities || []).find((a: any) => a.id === activityId);
      code = act?.code || '';
    }
    return docTypes.find(dt => dt.code === code)?.id || 0;
  };

  const startEditDoc = (doc: any, activityId: number) => {
    const currentTypeId = resolveDocTypeId(doc, activityId);
    setEditDocId(doc.id);
    setEditDocInitialTypeId(currentTypeId);
    setEditDocData({ document_type_id: currentTypeId, title: doc.title || '', notes: doc.notes || '', start_date: toDateStr(doc.start_date), end_date: toDateStr(doc.end_date), nr_registratura: doc.nr_registratura || '', data_registratura: toDateStr(doc.data_registratura), data_document: toDateStr(doc.data_document), data_expirare: toDateStr(doc.data_expirare) });
    setEditDocFiles([]);
    setEditDocActId(activityId);
    if (expandedDetail) {
      const ids: number[] = [];
      const sameGroup = (a: any, b: any) =>
        (a.title || '') === (b.title || '') &&
        (a.notes || '') === (b.notes || '') &&
        (a.nr_registratura || '') === (b.nr_registratura || '') &&
        toDateStr(a.data_registratura) === toDateStr(b.data_registratura) &&
        toDateStr(a.data_document) === toDateStr(b.data_document) &&
        toDateStr(a.data_expirare) === toDateStr(b.data_expirare) &&
        toDateStr(a.start_date) === toDateStr(b.start_date) &&
        toDateStr(a.end_date) === toDateStr(b.end_date);
      for (const act of (expandedDetail.activities || [])) {
        if (act.id !== activityId) continue;
        for (const d of (act.documents || [])) {
          if (sameGroup(d, doc)) ids.push(d.id);
        }
      }
      setEditDocGroupIds(ids.length > 1 ? ids : [doc.id]);
    } else {
      setEditDocGroupIds([doc.id]);
    }
  };

  const handleSaveDoc = async () => {
    if (!editDocId) return;
    setSavingDoc(true);
    try {
      // Tipul se trimite doar dacă a fost schimbat, ca să nu suprascrie tipul existent.
      const { document_type_id, ...docFields } = editDocData;
      const typeChanged = document_type_id > 0 && document_type_id !== editDocInitialTypeId;
      const payload = typeChanged ? { ...docFields, document_type_id } : docFields;

      for (const id of editDocGroupIds) {
        await updateProcurementDocument(id, payload);
      }
      if (editDocFiles.length > 0 && editDocActId) {
        for (const file of editDocFiles) {
          const result = await uploadActivityDocument(editDocActId, file);
          if (result && (result as any).document?.id) {
            await updateProcurementDocument((result as any).document.id, payload);
          }
        }
      }
      setEditDocId(null);
      setEditDocGroupIds([]);
      setEditDocFiles([]);
      setEditDocActId(null);
      setEditDocInitialTypeId(0);
      if (expanded) await loadDetail(expanded);
      toast.success('Document actualizat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu s-a putut salva documentul. Încearcă din nou.');
    }
    finally { setSavingDoc(false); }
  };

  const handleAddDocuments = async () => {
    if (!addDocProcId || !addDocForm.document_type_id || addDocFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of addDocFiles) {
        await addProcurementDocument(addDocProcId, file, {
          document_type_id: addDocForm.document_type_id,
          title: addDocForm.title || undefined,
          notes: addDocForm.notes || undefined,
          start_date: addDocForm.start_date || undefined,
          end_date: addDocForm.end_date || undefined,
          nr_registratura: addDocForm.nr_registratura || undefined,
          data_registratura: addDocForm.data_registratura || undefined,
          data_document: addDocForm.data_document || undefined,
          data_expirare: addDocForm.data_expirare || undefined,
        });
      }
      setAddDocProcId(null);
      setAddDocForm({ document_type_id: 0, title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
      setAddDocFiles([]);
      await loadProcurements();
      if (expanded === addDocProcId) await loadDetail(addDocProcId);
      onRefresh();
      toast.success(addDocFiles.length > 1 ? 'Fișiere încărcate.' : 'Fișier încărcat.');
    } catch (err) {
      console.error(err);
      toast.error('Nu s-au putut încărca fișierele. Încearcă din nou.');
    }
    finally { setUploading(false); }
  };

  // Deschide modalul „Adaugă document" pre-țintit pe o activitate: preselectează tipul
  // documentului după codul activității, ca butonul de upload de pe fiecare activitate
  // (gol sau nu) să folosească fluxul existent de încărcare fără pași suplimentari.
  const openAddDocForActivity = (procId: number, act: ProcurementActivity) => {
    const typeId = docTypes.find(dt => dt.code === (act as any).code)?.id || 0;
    setAddDocDetailsOpen(false);
    setAddDocProcId(procId);
    setAddDocTypeLocked(true); // deschis pentru un tip anume → tipul rămâne fix
    setAddDocForm({ document_type_id: typeId, title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' });
    setAddDocFiles([]);
  };

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  const renderProcFormFields = (form: typeof wizardData, setForm: (fn: (f: typeof wizardData) => typeof wizardData) => void) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Denumire achiziție *</label>
        <input value={form.name} onChange={e => setForm(d => ({ ...d, name: e.target.value }))}
          placeholder="ex: Servicii proiectare SF DJ 107" className={inputClass} autoFocus />
        <p className="text-xs text-gray-400 mt-1">Singurul câmp obligatoriu. Restul pot fi completate acum sau mai târziu.</p>
      </div>

      <button
        type="button"
        onClick={() => setProcDetailsOpen(o => !o)}
        className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
      >
        {procDetailsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Detalii suplimentare (opțional)
      </button>

      {procDetailsOpen && (
        <div className="space-y-4 border-l-2 border-gray-100 pl-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valoare estimată (RON)</label>
            <input type="number" value={form.estimated_value} onChange={e => setForm(d => ({ ...d, estimated_value: e.target.value }))}
              placeholder="opțional" className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data început" value={form.start_date} onChange={e => setForm(d => ({ ...d, start_date: e.target.value }))} />
            <DateInput label="Data finalizare" value={form.end_date} onChange={e => setForm(d => ({ ...d, end_date: e.target.value }))} />
          </div>
          <UserSelect
            projectId={projectId}
            value={form.responsible_user_id}
            onChange={userId => setForm(d => ({...d, responsible_user_id: userId}))}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={form.notes} onChange={e => setForm(d => ({ ...d, notes: e.target.value }))}
              rows={2} placeholder="opțional" className={inputClass} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 font-medium">{summary.total > 0 ? `${summary.completed}/${summary.total} dosare de achiziție` : 'Niciun dosar de achiziție'}</div>
        <Button variant="primary" size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setProcDetailsOpen(false); setShowAddModal(true); }}>
          Adaugă dosar de achiziție
        </Button>
      </div>

      {/* Add Procurement Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setWizardData({ name: '', estimated_value: '', notes: '', start_date: '', end_date: '', responsible_user_id: null }); }}
        title="Dosar nou de achiziție"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setShowAddModal(false)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleCreate} disabled={creating || !wizardData.name} isLoading={creating}>
              {creating ? 'Se creează...' : 'Creează'}
            </Button>
          </div>
        }
      >
        {renderProcFormFields(wizardData, setWizardData)}
      </Modal>

      {/* Edit Procurement Modal */}
      <Modal
        isOpen={editingId !== null}
        onClose={() => setEditingId(null)}
        title="Editare dosar de achiziție"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditingId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleUpdate} disabled={saving || !editData.name} isLoading={saving}>
              {saving ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        {renderProcFormFields(editData, setEditData)}
      </Modal>

      {/* Add Document Modal */}
      <Modal
        isOpen={addDocProcId !== null}
        onClose={() => { setAddDocProcId(null); setAddDocFiles([]); }}
        title="Adaugă document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setAddDocProcId(null); setAddDocFiles([]); }}>Anulează</Button>
            <Button
              variant="primary" size="md"
              onClick={handleAddDocuments}
              disabled={uploading || !addDocForm.document_type_id || addDocFiles.length === 0}
              isLoading={uploading}
              leftIcon={!uploading ? <Upload className="w-4 h-4" /> : undefined}
            >
              {uploading ? 'Se încarcă...' : `Încarcă${addDocFiles.length > 0 ? ` (${addDocFiles.length} fișier${addDocFiles.length > 1 ? 'e' : ''})` : ''}`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip document *</label>
            {addDocTypeLocked ? (
              // Deschis pentru un tip anume: se adaugă doar fișiere pe acel tip, nu se schimbă tipul.
              <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700">
                {(() => { const dt = docTypes.find(d => d.id === addDocForm.document_type_id); return dt ? `${dt.code} - ${dt.name}` : 'Tip document'; })()}
              </div>
            ) : (
              <SearchableSelect
                options={docTypes.map(dt => ({ value: dt.id, label: `${dt.code} - ${dt.name}` }))}
                value={addDocForm.document_type_id}
                onChange={v => setAddDocForm(d => ({ ...d, document_type_id: v === '' ? 0 : Number(v) }))}
                placeholder="-- Selectează tipul documentului --"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fișiere *</label>
            <FileDropArea files={addDocFiles} onFilesChange={setAddDocFiles} id="add-doc-files-modal" />
          </div>

          <button
            type="button"
            onClick={() => setAddDocDetailsOpen(o => !o)}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            {addDocDetailsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Detalii suplimentare (opțional)
          </button>

          {addDocDetailsOpen && (
            <div className="space-y-4 border-l-2 border-gray-100 pl-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
                <input value={addDocForm.title} onChange={e => setAddDocForm(d => ({ ...d, title: e.target.value }))} placeholder="Titlul documentului (opțional)" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
                <textarea value={addDocForm.notes} onChange={e => setAddDocForm(d => ({ ...d, notes: e.target.value }))} rows={2} placeholder="Observații (opțional)" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DateInput label="Data început" tooltip="Perioada de valabilitate/execuție — începutul" value={addDocForm.start_date} onChange={e => setAddDocForm(d => ({ ...d, start_date: e.target.value }))} />
                <DateInput label="Data finalizare" tooltip="Perioada de valabilitate/execuție — sfârșitul" value={addDocForm.end_date} onChange={e => setAddDocForm(d => ({ ...d, end_date: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <span>Nr. registratură</span>
                    <span title="Numărul unic atribuit de registratura instituției la înregistrarea documentului" className="cursor-help text-gray-400 hover:text-gray-600">
                      <HelpCircle className="w-3.5 h-3.5" />
                    </span>
                  </label>
                  <input value={addDocForm.nr_registratura} onChange={e => setAddDocForm(d => ({ ...d, nr_registratura: e.target.value }))} placeholder="Nr. registratură" className={inputClass} />
                </div>
                <DateInput label="Data registratură" tooltip="Data la care documentul a fost înregistrat la registratura instituției" value={addDocForm.data_registratura} onChange={e => setAddDocForm(d => ({ ...d, data_registratura: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DateInput label="Data document" tooltip="Data emiterii/semnării documentului de către emitent" value={addDocForm.data_document} onChange={e => setAddDocForm(d => ({ ...d, data_document: e.target.value }))} />
                <DateInput label="Data expirare" tooltip="Data până la care documentul este valabil (pentru oferte, garanții, autorizații)" value={addDocForm.data_expirare} onChange={e => setAddDocForm(d => ({ ...d, data_expirare: e.target.value }))} />
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Document Modal */}
      <Modal
        isOpen={editDocId !== null}
        onClose={() => { setEditDocId(null); setEditDocFiles([]); }}
        title="Editare document"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setEditDocId(null); setEditDocFiles([]); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveDoc} disabled={savingDoc} isLoading={savingDoc}
              leftIcon={!savingDoc ? <Upload className="w-4 h-4" /> : undefined}>
              {savingDoc ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip document</label>
            <SearchableSelect
              options={docTypes.map(dt => ({ value: dt.id, label: `${dt.code} - ${dt.name}` }))}
              value={editDocData.document_type_id}
              onChange={v => setEditDocData(d => ({ ...d, document_type_id: v === '' ? 0 : Number(v) }))}
              placeholder="-- Selectează tipul documentului --"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
            <input value={editDocData.title} onChange={e => setEditDocData(d => ({ ...d, title: e.target.value }))} placeholder="Titlul documentului" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
            <textarea value={editDocData.notes} onChange={e => setEditDocData(d => ({ ...d, notes: e.target.value }))} rows={2} placeholder="Observații" className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data început" tooltip="Perioada de valabilitate/execuție — începutul" value={editDocData.start_date} onChange={e => setEditDocData(d => ({ ...d, start_date: e.target.value }))} />
            <DateInput label="Data finalizare" tooltip="Perioada de valabilitate/execuție — sfârșitul" value={editDocData.end_date} onChange={e => setEditDocData(d => ({ ...d, end_date: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <span>Nr. registratură</span>
                <span title="Numărul unic atribuit de registratura instituției la înregistrarea documentului" className="cursor-help text-gray-400 hover:text-gray-600">
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              </label>
              <input value={editDocData.nr_registratura} onChange={e => setEditDocData(d => ({ ...d, nr_registratura: e.target.value }))} placeholder="Nr. registratură" className={inputClass} />
            </div>
            <DateInput label="Data registratură" tooltip="Data la care documentul a fost înregistrat la registratura instituției" value={editDocData.data_registratura} onChange={e => setEditDocData(d => ({ ...d, data_registratura: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="Data document" tooltip="Data emiterii/semnării documentului de către emitent" value={editDocData.data_document} onChange={e => setEditDocData(d => ({ ...d, data_document: e.target.value }))} />
            <DateInput label="Data expirare" tooltip="Data până la care documentul este valabil (pentru oferte, garanții, autorizații)" value={editDocData.data_expirare} onChange={e => setEditDocData(d => ({ ...d, data_expirare: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adaugă fișiere noi</label>
            <FileDropArea files={editDocFiles} onFilesChange={setEditDocFiles} id="edit-doc-files-modal" compact />
          </div>
        </div>
      </Modal>

      {initialLoading ? (
        <div className="text-center py-8 text-sm text-gray-500">Se încarcă...</div>
      ) : procurements.length === 0 ? (
        <EmptyState
          icon="folder"
          title="Niciun dosar de achiziție"
          description="Achiziția este primul pas al dosarului investiției. Creează un dosar de achiziție, apoi vei putea încărca documente și lega contractul rezultat."
          action={{ label: 'Adaugă dosar de achiziție', onClick: () => { setProcDetailsOpen(false); setShowAddModal(true); } }}
        />
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <div className="divide-y divide-gray-100">
          {procurements.map(proc => {
            const isExpanded = expanded === proc.id;
            const completedCount = parseInt(String(proc.completed_activities)) || 0;
            const totalCount = parseInt(String(proc.total_activities)) || 0;
            const linkedContracts = proc.linked_contracts || [];

            return (
              <div key={proc.id} className="bg-white">
                <div className="flex items-center hover:bg-blue-50 transition-colors cursor-pointer group">
                  <button
                    onClick={() => {
                      if (isExpanded) { setExpanded(null); setExpandedDetail(null); }
                      else { setExpanded(proc.id); loadDetail(proc.id); }
                    }}
                    className="flex-1 flex items-center gap-3 px-4 py-3 text-left"
                  >
                    <span className="p-0.5 rounded hover:bg-gray-200 transition-colors">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    </span>
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${completedCount === totalCount && totalCount > 0 ? 'bg-green-500' : completedCount > 0 ? 'bg-yellow-500' : 'bg-gray-300'}`} />
                    <span className="flex-1 text-sm font-medium text-gray-900">{proc.name}</span>
                    {proc.responsible_name && (
                      <span className="text-xs text-gray-400 flex-shrink-0">{proc.responsible_name}</span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{completedCount}/{totalCount} activități</span>
                  </button>
                  <div className="flex items-center gap-1 pr-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); setAddDocDetailsOpen(false); setAddDocProcId(proc.id); setAddDocTypeLocked(false); setAddDocForm({ document_type_id: 0, title: '', notes: '', start_date: '', end_date: '', nr_registratura: '', data_registratura: '', data_document: '', data_expirare: '' }); setAddDocFiles([]); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-green-700 border border-green-200 hover:bg-green-50 transition-colors" title="Adaugă document"
                    >
                      <Upload className="w-4 h-4" />
                      Adaugă document
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); startEdit(proc); }}
                      className="p-1.5 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors" title="Editează">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(proc); }}
                      className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors" title="Șterge">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Contract(e) generat(e) din această achiziție — pasul următor din lanț */}
                <div className="px-4 pb-2 pl-11">
                  {linkedContracts.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {linkedContracts.map(c => (
                        <span
                          key={c.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700 border border-indigo-100"
                          title="Contract generat din această achiziție"
                        >
                          <FileText className="w-3 h-3 flex-shrink-0" />
                          Contract: {c.contract_number ? `${c.contract_number} — ` : ''}{c.contract_name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">Fără contract atribuit încă</div>
                  )}
                </div>

                {isExpanded && expandedDetail && (
                  <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                    <div className="space-y-3 mt-3">
                      {(expandedDetail.activities || []).map((act: ProcurementActivity) => {
                        const docs = act.documents || [];
                        const groups: { key: string; title: string; notes: string; nr_registratura: string; data_registratura: string; data_document: string; data_expirare: string; start_date: string; end_date: string; docs: any[] }[] = [];
                        for (const doc of docs) {
                          const gKey = [doc.title || '', doc.notes || '', doc.nr_registratura || '', toDateStr(doc.data_registratura), toDateStr(doc.data_document), toDateStr(doc.data_expirare), toDateStr(doc.start_date), toDateStr(doc.end_date)].join('|||');
                          const existing = groups.find(g => g.key === gKey);
                          if (existing) { existing.docs.push(doc); }
                          else { groups.push({ key: gKey, title: doc.title || '', notes: doc.notes || '', nr_registratura: doc.nr_registratura || '', data_registratura: toDateStr(doc.data_registratura), data_document: toDateStr(doc.data_document), data_expirare: toDateStr(doc.data_expirare), start_date: toDateStr(doc.start_date), end_date: toDateStr(doc.end_date), docs: [doc] }); }
                        }
                        return (
                          <div key={act.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-primary-500 flex-shrink-0" />
                              <span className="text-xs font-mono text-gray-400 mr-1">{act.code}</span>
                              <span className="text-sm font-semibold text-gray-700">{act.name}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 ml-auto">
                                {docs.length} {docs.length === 1 ? 'fișier' : 'fișiere'}
                              </span>
                              <button
                                onClick={() => openAddDocForActivity(proc.id, act)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors flex-shrink-0"
                                title="Încarcă fișier pentru această activitate"
                              >
                                <Upload className="w-3.5 h-3.5" />
                                Încarcă fișier
                              </button>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {docs.length === 0 ? (
                                <div className="px-4 py-4 flex flex-col items-center gap-2 text-center">
                                  <span className="text-sm text-gray-400 italic">Niciun document încărcat</span>
                                  <button
                                    onClick={() => openAddDocForActivity(proc.id, act)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors"
                                  >
                                    <Upload className="w-4 h-4" />
                                    Încarcă fișier
                                  </button>
                                </div>
                              ) : (
                                groups.map((group) => {
                                  const fmtDate = (s: string) => s ? new Date(s).toLocaleDateString('ro-RO') : '';
                                  const hasMeta = group.nr_registratura || group.data_registratura || group.data_document || group.data_expirare || group.start_date || group.end_date;
                                  return (
                                  <div key={group.key} className="px-4 py-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="flex-1 min-w-0">
                                        {group.title && <div className="text-sm text-gray-800 font-medium">{group.title}</div>}
                                        {group.notes && <div className="text-xs text-gray-500 mt-0.5">{group.notes}</div>}
                                        {!group.title && !group.notes && <div className="text-xs text-gray-400 italic">Fără titlu</div>}
                                        {hasMeta && (
                                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {group.nr_registratura && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100" title="Nr. registratură">
                                                Nr. {group.nr_registratura}{group.data_registratura ? ` / ${fmtDate(group.data_registratura)}` : ''}
                                              </span>
                                            )}
                                            {!group.nr_registratura && group.data_registratura && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100" title="Data registratură">
                                                Reg. {fmtDate(group.data_registratura)}
                                              </span>
                                            )}
                                            {group.data_document && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200" title="Data document">
                                                Doc. {fmtDate(group.data_document)}
                                              </span>
                                            )}
                                            {group.data_expirare && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-100" title="Data expirare">
                                                Exp. {fmtDate(group.data_expirare)}
                                              </span>
                                            )}
                                            {(group.start_date || group.end_date) && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-100" title="Perioadă">
                                                {fmtDate(group.start_date) || '—'} → {fmtDate(group.end_date) || '—'}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <button onClick={() => startEditDoc(group.docs[0], act.id)}
                                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
                                          <Pencil className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="space-y-1.5">
                                      {group.docs.map((doc: any) => (
                                        <div key={doc.id} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2 hover:bg-blue-50 transition-colors">
                                          <FileUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                          <span className="flex-1 truncate text-gray-700 font-medium">{doc.file_name}</span>
                                          <span className="text-xs text-gray-400 flex-shrink-0">{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}</span>
                                          <div className="flex items-center gap-1 flex-shrink-0">
                                            {doc.attachment_id ? (
                                              <AddActivityForDocumentButton
                                                projectId={projectId}
                                                attachmentId={doc.attachment_id}
                                                fileName={doc.file_name}
                                                docTitle={group.title || undefined}
                                                variant="icon"
                                                size="sm"
                                                onCreated={() => loadDetail(proc.id)}
                                              />
                                            ) : null}
                                            {doc.file_name?.toLowerCase().endsWith('.pdf') && (
                                              <button onClick={() => window.open(`/auth/files/${doc.attachment_id}/${encodeURIComponent(doc.file_name)}?inline=1`, '_blank')}
                                                className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Vizualizează">
                                                <Eye className="w-4 h-4" />
                                              </button>
                                            )}
                                            <button onClick={() => downloadAttachmentFile(doc.attachment_id, doc.file_name)}
                                              className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-100 transition-colors" title="Descarcă">
                                              <Download className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => setPendingDocDelete({ action: () => handleDeleteDoc(doc.id), message: `Ești sigur că vrei să ștergi fișierul "${doc.file_name}"?` })}
                                              className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge fișier">
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}
      <ConfirmDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) handleDelete(deleteTarget); }}
        message="Ești sigur că vrei să ștergi acest dosar de achiziție? Toate documentele asociate vor fi șterse."
      />
      <ConfirmDelete
        isOpen={!!pendingDocDelete}
        onClose={() => setPendingDocDelete(null)}
        onConfirm={() => { if (pendingDocDelete) pendingDocDelete.action(); }}
        message={pendingDocDelete?.message}
      />
    </div>
  );
}
